import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  BriefcaseBusiness,
  Building2,
  Check,
  CheckCircle2,
  ChevronRight,
  ClipboardCopy,
  Clock3,
  HeartHandshake,
  House,
  ListChecks,
  LoaderCircle,
  LockKeyhole,
  MessageCircleQuestion,
  MessageSquareText,
  Phone,
  RefreshCw,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  Store,
  Trash2,
  UserRound,
  UsersRound,
  Video,
} from "lucide-react";
import {
  useEffect,
  useLayoutEffect,
  useState,
} from "react";
import { ConfirmDialog, useToast } from "@tribunalea/ui";
import { CivitaAssistantLauncher } from "../components/CivitaAssistant.jsx";

const relationships = Object.freeze([
  { id: "work", label: "Trabajo", description: "Jefe, colega, empleado o cliente", icon: BriefcaseBusiness },
  { id: "housing", label: "Vivienda", description: "Propietario, inquilino, vecino o consorcio", icon: House },
  { id: "purchase_service", label: "Compra o servicio", description: "Comercio, empresa o prestador", icon: Store },
  { id: "family_personal", label: "Personal o familiar", description: "Familia, amistad o vínculo cercano", icon: UsersRound },
  { id: "institution", label: "Institución", description: "Escuela, obra social u organismo", icon: Building2 },
  { id: "professional_service", label: "Con un profesional", description: "Alguien a quien consultaste o contrataste", icon: UserRound },
  { id: "other", label: "Otra situación", description: "Cualquier otro vínculo", icon: MessageSquareText },
]);

const formats = Object.freeze([
  { id: "in_person", label: "En persona", icon: UsersRound },
  { id: "phone", label: "Por teléfono", icon: Phone },
  { id: "video", label: "Videollamada", icon: Video },
  { id: "written_message", label: "Por escrito", icon: MessageSquareText },
]);

const urgencyOptions = Object.freeze([
  { id: "flexible", label: "Sin fecha definida" },
  { id: "soon", label: "En los próximos días" },
  { id: "today", label: "Hoy" },
]);

const safetyOptions = Object.freeze([
  { id: "none", label: "No", description: "Es incómoda, pero me siento seguro/a." },
  { id: "uncomfortable", label: "Un poco", description: "Me preocupa que haya presión o maltrato verbal." },
  { id: "afraid", label: "Sí", description: "Me da miedo la reacción o estar a solas con esa persona." },
]);

const demoPreparation = Object.freeze({
  id: "preparacion-ejemplo",
  counterpart: "Mi vecino del departamento de arriba",
  relationship: "housing",
  situation: "Hace varias semanas hay ruidos fuertes de madrugada. Ya lo mencioné una vez en el ascensor, pero no pudimos hablar con calma.",
  goal: "Acordar horarios de descanso y una forma de avisarnos si vuelve a pasar.",
  concern: "Me preocupa que se ponga a la defensiva y la conversación termine en una discusión.",
  previousAttempts: "Lo mencioné brevemente una vez.",
  format: "in_person",
  urgency: "soon",
  safetyConcern: "none",
  analysisStatus: "ready",
  status: "ready",
  createdAt: new Date().toISOString(),
  plan: {
    title: "Hablar sobre los ruidos sin convertirlo en una pelea",
    situationSummary: "Querés conversar con tu vecino por ruidos reiterados durante la madrugada y llegar a un acuerdo práctico para descansar mejor.",
    primaryGoal: "Acordar horarios de descanso y una manera simple de avisarse si vuelve a ocurrir.",
    privateAnchor: "Quiero resolver el problema, no demostrar quién tiene razón.",
    recommendedTone: {
      label: "Calmo y directo",
      explanation: "Nombrá hechos concretos, explicá cómo te afectan y hacé un pedido realizable sin atribuir intenciones.",
    },
    recommendedSetting: "Buscá un momento tranquilo, fuera del horario del problema y sin apuro. Evitá iniciar la conversación mientras el ruido está ocurriendo o cuando estés muy enojado/a.",
    openingLine: "¿Tenés unos minutos? Quería que hablemos con calma de los ruidos que se vienen escuchando de madrugada y ver si podemos encontrar una forma de resolverlo entre los dos.",
    keyPoints: [
      { title: "Describí el hecho", phrase: "En las últimas semanas escuché ruidos fuertes varias noches durante la madrugada.", purpose: "Mantener la conversación en hechos observables." },
      { title: "Explicá el impacto", phrase: "Me está costando dormir y al día siguiente se me hace difícil descansar bien.", purpose: "Mostrar por qué necesitás un cambio sin atacar." },
      { title: "Hacé un pedido concreto", phrase: "¿Podríamos acordar bajar el volumen durante la noche y avisarnos por mensaje si hay alguna situación excepcional?", purpose: "Proponer una solución que ambos puedan entender." },
    ],
    questionsToAsk: [
      { question: "¿Hay algo de mi departamento que también te esté molestando?", purpose: "Abrir un intercambio de buena fe." },
      { question: "¿Qué horario te parece razonable para cuidar el descanso?", purpose: "Construir un acuerdo concreto." },
    ],
    difficultMoments: [
      { situation: "Si dice que estás exagerando", response: "Puede ser que lo vivamos distinto. Yo te cuento el efecto que está teniendo en mi descanso y me gustaría que busquemos una solución concreta." },
      { situation: "Si sube el tono", response: "Prefiero que lo hablemos cuando podamos hacerlo con calma. Podemos retomarlo en otro momento." },
    ],
    boundaries: [
      { title: "Si la conversación se vuelve agresiva", phrase: "No voy a seguir hablando si nos tratamos mal. Lo retomamos cuando estemos más tranquilos.", purpose: "Cuidar el tono y poder retirarte sin amenazar." },
    ],
    phrasesToAvoid: [
      { avoid: "Vos siempre hacés lo que querés.", prefer: "Escuché ruidos fuertes varias noches de madrugada.", reason: "Evita generalizaciones y se concentra en el problema concreto." },
    ],
    closingLine: "Entonces, probamos con ese horario y, si vuelve a pasar, nos avisamos por mensaje. Gracias por hablarlo.",
    beforeChecklist: ["Elegí un momento tranquilo", "Tené claro el pedido principal", "Recordá dos ejemplos concretos, sin hacer una lista interminable"],
    afterChecklist: ["Anotá brevemente qué acordaron", "Cumplí también lo que hayas aceptado", "Si no mejora, decidí el próximo paso con calma"],
    ifNoAgreement: ["Cerrá la conversación sin discutir", "Dejá por escrito tu pedido de forma breve", "Buscá una instancia apropiada de ayuda si el problema continúa"],
    safety: {
      level: "standard",
      message: "No mencionaste un temor por tu seguridad. Aun así, podés terminar la conversación si deja de ser respetuosa.",
      steps: [],
    },
    professionalSupportRecommended: false,
    professionalSupportReason: null,
  },
});

function formatDate(value) {
  if (!value) return "";
  return new Intl.DateTimeFormat("es-AR", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(new Date(value));
}

function formatLabel(items, value) {
  return items.find(({ id }) => id === value)?.label || value;
}

function planAsText(preparation) {
  const { plan } = preparation;
  const lines = [
    plan.title,
    "",
    `Objetivo: ${plan.primaryGoal}`,
    `Recordatorio: ${plan.privateAnchor}`,
    `Tono: ${plan.recommendedTone.label} — ${plan.recommendedTone.explanation}`,
    `Momento y lugar: ${plan.recommendedSetting}`,
    "",
    "Cómo empezar",
    plan.openingLine,
    "",
    "Puntos principales",
    ...plan.keyPoints.map((item) => `- ${item.title}: ${item.phrase}`),
    "",
    "Preguntas",
    ...plan.questionsToAsk.map((item) => `- ${item.question}`),
    "",
    "Cómo cerrar",
    plan.closingLine,
  ];
  return lines.join("\n");
}

function ProcessingView({ preparation, onBack }) {
  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [preparation.id]);
  return (
    <main className="civita-document-page conversation-page conversation-processing-view">
      <button className="text-button" type="button" onClick={onBack}><ArrowLeft size={16} /> Volver</button>
      <section className="processing-card" aria-live="polite">
        <span className="processing-card__icon"><Sparkles size={25} /></span>
        <span className="eyebrow">Preparar una conversación difícil</span>
        <h1>Estamos preparando una guía para vos</h1>
        <p>Ordenamos tu objetivo, los puntos importantes y frases que te ayuden a hablar con claridad.</p>
        <ol>
          <li className="processing-step is-done"><span><Check size={15} /></span>Situación y objetivo recibidos</li>
          <li className="processing-step is-active"><span><LoaderCircle className="is-spinning" size={15} /></span>Preparando frases, preguntas y límites</li>
          <li className="processing-step is-pending"><span />Revisando que el plan sea claro y seguro</li>
        </ol>
        <small>No contactaremos a la otra persona ni enviaremos ningún mensaje.</small>
      </section>
    </main>
  );
}

function ErrorView({ preparation, retrying, onRetry, onBack }) {
  return (
    <main className="civita-document-page conversation-page conversation-processing-view">
      <section className="processing-card is-error">
        <span className="processing-card__icon"><AlertCircle size={25} /></span>
        <span className="eyebrow">No pudimos terminar</span>
        <h1>Necesitamos intentar otra vez</h1>
        <p>{preparation.error?.message || "No pudimos preparar la conversación."}</p>
        <div className="button-row">
          <button className="primary-button" type="button" onClick={onRetry} disabled={retrying}>{retrying ? <LoaderCircle className="is-spinning" size={17} /> : <RefreshCw size={17} />}{retrying ? "Reintentando…" : "Reintentar"}</button>
          <button className="secondary-button" type="button" onClick={onBack}>Empezar otra</button>
        </div>
      </section>
    </main>
  );
}

function PhraseCard({ item, number }) {
  return (
    <article className="conversation-phrase-card">
      {number && <span>{number}</span>}
      <div><h3>{item.title}</h3><blockquote>{item.phrase}</blockquote><small>{item.purpose}</small></div>
    </article>
  );
}

function ResultView({ preparation, onBack, onDelete }) {
  const { showToast } = useToast();
  const { plan } = preparation;
  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [preparation.id]);

  async function copyPlan() {
    try {
      await navigator.clipboard.writeText(planAsText(preparation));
      showToast({ message: "Copiamos el plan.", tone: "success" });
    } catch {
      showToast({ message: "No pudimos copiar el plan.", tone: "danger" });
    }
  }

  return (
    <main className="civita-document-page conversation-page conversation-result-page">
      {preparation.demo && <div className="demo-banner"><Sparkles size={16} /> Este es un ejemplo ilustrativo.</div>}
      <header className="result-header conversation-result-header">
        <button className="text-button" type="button" onClick={onBack}><ArrowLeft size={16} /> Mis preparaciones</button>
        <div>
          <button className="secondary-button" type="button" onClick={copyPlan}><ClipboardCopy size={16} /> Copiar plan</button>
          {!preparation.demo && <button className="danger-icon-button" type="button" onClick={onDelete} aria-label="Eliminar preparación"><Trash2 size={17} /></button>}
        </div>
      </header>

      <section className="conversation-result-heading">
        <div><span className="eyebrow">Tu guía está lista</span><h1>{plan.title}</h1><p>{plan.situationSummary}</p></div>
        <span className="analysis-ready"><CheckCircle2 size={16} /> Plan preparado</span>
      </section>

      {plan.safety.level !== "standard" && (
        <section className={`conversation-safety is-${plan.safety.level}`}>
          <span className="section-icon"><ShieldAlert size={19} /></span>
          <div><span className="eyebrow">Tu seguridad primero</span><h2>{plan.safety.message}</h2>{plan.safety.steps.length > 0 && <ul>{plan.safety.steps.map((step) => <li key={step}>{step}</li>)}</ul>}</div>
        </section>
      )}

      <section className="conversation-compass">
        <article><span><MessageCircleQuestion size={18} /></span><div><small>Tu objetivo</small><h2>{plan.primaryGoal}</h2></div></article>
        <article><span><HeartHandshake size={18} /></span><div><small>Tono sugerido</small><h2>{plan.recommendedTone.label}</h2><p>{plan.recommendedTone.explanation}</p></div></article>
        <article><span><ShieldCheck size={18} /></span><div><small>Tu recordatorio</small><h2>“{plan.privateAnchor}”</h2></div></article>
        <article><span><Clock3 size={18} /></span><div><small>Momento y lugar</small><p>{plan.recommendedSetting}</p></div></article>
      </section>

      <section className="conversation-script-section">
        <header><span className="section-icon"><MessageSquareText size={19} /></span><div><span className="eyebrow">Cómo decirlo</span><h2>Una conversación con principio, puntos claros y cierre</h2></div></header>
        <article className="conversation-opening"><small>Podés empezar así</small><blockquote>“{plan.openingLine}”</blockquote></article>
        <div className="conversation-key-points">{plan.keyPoints.map((item, index) => <PhraseCard item={item} number={index + 1} key={`${item.title}-${index}`} />)}</div>
        <article className="conversation-closing"><small>Podés cerrar así</small><blockquote>“{plan.closingLine}”</blockquote></article>
      </section>

      <div className="conversation-two-column">
        <section className="conversation-list-section">
          <header><MessageCircleQuestion size={18} /><div><span className="eyebrow">Para escuchar también</span><h2>Preguntas que pueden ayudar</h2></div></header>
          {plan.questionsToAsk.length ? <ol>{plan.questionsToAsk.map((item, index) => <li key={`${item.question}-${index}`}><span>{index + 1}</span><div><strong>{item.question}</strong><small>{item.purpose}</small></div></li>)}</ol> : <p className="conversation-empty">No necesitás preparar preguntas adicionales para empezar.</p>}
        </section>
        <section className="conversation-list-section">
          <header><ShieldCheck size={18} /><div><span className="eyebrow">Si se complica</span><h2>Respuestas para momentos difíciles</h2></div></header>
          {plan.difficultMoments.length ? <div className="conversation-moments">{plan.difficultMoments.map((item, index) => <article key={`${item.situation}-${index}`}><strong>{item.situation}</strong><p>“{item.response}”</p></article>)}</div> : <p className="conversation-empty">No identificamos un momento particular que necesites ensayar.</p>}
        </section>
      </div>

      {(plan.boundaries.length > 0 || plan.phrasesToAvoid.length > 0) && (
        <div className="conversation-two-column">
          {plan.boundaries.length > 0 && <section className="conversation-boundaries"><header><ShieldAlert size={18} /><div><span className="eyebrow">Tus límites</span><h2>Qué podés hacer si necesitás frenar</h2></div></header><div>{plan.boundaries.map((item, index) => <PhraseCard item={item} key={`${item.title}-${index}`} />)}</div></section>}
          {plan.phrasesToAvoid.length > 0 && <section className="conversation-avoid"><header><MessageSquareText size={18} /><div><span className="eyebrow">Cuidar el tono</span><h2>Frases que conviene cambiar</h2></div></header><div>{plan.phrasesToAvoid.map((item, index) => <article key={`${item.avoid}-${index}`}><div><small>En vez de</small><p>“{item.avoid}”</p></div><ArrowRight size={16} /><div><small>Probá con</small><p>“{item.prefer}”</p></div><span>{item.reason}</span></article>)}</div></section>}
        </div>
      )}

      <section className="conversation-checklists">
        <header><span className="section-icon"><ListChecks size={18} /></span><div><span className="eyebrow">Tu hoja de ruta</span><h2>Antes, si no hay acuerdo y después</h2></div></header>
        <div>
          {[
            ["Antes de hablar", plan.beforeChecklist],
            ["Si no llegan a un acuerdo", plan.ifNoAgreement],
            ["Después de hablar", plan.afterChecklist],
          ].map(([title, items]) => <section key={title}><h3>{title}</h3><ul>{items.map((item) => <li key={item}><Check size={14} /> {item}</li>)}</ul></section>)}
        </div>
      </section>

      {plan.professionalSupportRecommended && plan.professionalSupportReason && (
        <section className="professional-note conversation-professional-note"><UserRound size={20} /><div><strong>Puede ser útil buscar apoyo profesional</strong><p>{plan.professionalSupportReason}</p></div></section>
      )}

      <CivitaAssistantLauncher
        className="conversation-rehearsal"
        eyebrow="Ensayá con Justiniano"
        title="Practicá antes de hablar"
        description="Abrí el chat flotante para probar tu apertura, representar a la otra persona o ajustar una frase."
        sourceLabel="Solo esta preparación"
        conversationScope={`conversacion-dificil:${preparation.id}:v1`}
        conversationMetadata={{ civitaConversationPreparationId: preparation.id }}
        welcome="Ya tengo tu guía. Podemos ensayar la conversación paso a paso o ajustar una frase para que suene natural para vos."
        suggestions={["Ensayemos cómo empezar", "Representá a la otra persona", "Ayudame si se pone a la defensiva"]}
        appContext={{
          route: "/preparar-conversacion",
          activeEntity: { type: "civita_conversation_preparation", id: preparation.id, label: plan.title },
          conversationPreparation: {
            counterpart: preparation.counterpart,
            situation: preparation.situation,
            goal: preparation.goal,
            concern: preparation.concern,
            previousAttempts: preparation.previousAttempts,
            format: preparation.format,
            urgency: preparation.urgency,
            safetyConcern: preparation.safetyConcern,
            plan,
          },
        }}
      />

      <p className="ai-disclaimer">Esta guía fue generada con IA y puede equivocarse. Adaptá las frases a tu forma de hablar y priorizá siempre tu seguridad.</p>
    </main>
  );
}

export function PrepareConversationPage({ api, authEnabled }) {
  const { showToast } = useToast();
  const [counterpart, setCounterpart] = useState("");
  const [relationship, setRelationship] = useState("");
  const [situation, setSituation] = useState("");
  const [goal, setGoal] = useState("");
  const [concern, setConcern] = useState("");
  const [previousAttempts, setPreviousAttempts] = useState("");
  const [format, setFormat] = useState("in_person");
  const [urgency, setUrgency] = useState("flexible");
  const [safetyConcern, setSafetyConcern] = useState("none");
  const [preparations, setPreparations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(authEnabled);
  const [submitting, setSubmitting] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  function selectPreparation(preparation) {
    setSelected(preparation);
    if (!preparation?.demo) window.history.replaceState({}, "", `/preparar-conversacion?preparacion=${encodeURIComponent(preparation.id)}`);
  }

  function resetView() {
    setSelected(null);
    setDeleteOpen(false);
    setCounterpart("");
    setRelationship("");
    setSituation("");
    setGoal("");
    setConcern("");
    setPreviousAttempts("");
    setFormat("in_person");
    setUrgency("flexible");
    setSafetyConcern("none");
    window.history.replaceState({}, "", "/preparar-conversacion");
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }

  useEffect(() => {
    if (!authEnabled) return undefined;
    let active = true;
    api.listConversationPreparations()
      .then((items) => {
        if (!active) return;
        setPreparations(items);
        const requestedId = new URLSearchParams(window.location.search).get("preparacion");
        const match = items.find(({ id }) => id === requestedId);
        if (match) setSelected(match);
      })
      .catch((error) => showToast({ message: error.message, tone: "danger" }))
      .finally(() => active && setLoadingHistory(false));
    return () => { active = false; };
  }, [api, authEnabled, showToast]);

  useEffect(() => {
    if (!selected || selected.demo || selected.status !== "analyzing") return undefined;
    let active = true;
    let timer;
    async function refresh() {
      try {
        let current = await api.getConversationPreparation(selected.id);
        if (current.analysisStatus === "waiting") current = await api.analyzeConversationPreparation(current.id);
        if (!active) return;
        setSelected(current);
        setPreparations((items) => items.map((item) => item.id === current.id ? current : item));
        if (current.status === "analyzing") timer = window.setTimeout(refresh, 1_500);
      } catch (error) {
        if (!active) return;
        setSelected((current) => current ? { ...current, status: "failed", error: { code: error.code, message: error.message } } : current);
      }
    }
    void refresh();
    return () => { active = false; window.clearTimeout(timer); };
  }, [api, selected?.demo, selected?.id, selected?.status]);

  async function submit(event) {
    event.preventDefault();
    if (!relationship || counterpart.trim().length < 2 || situation.trim().length < 20 || goal.trim().length < 5 || submitting) return;
    if (!authEnabled) {
      selectPreparation({ ...demoPreparation, demo: true });
      return;
    }
    setSubmitting(true);
    try {
      const created = await api.createConversationPreparation({
        counterpart,
        relationship,
        situation,
        goal,
        concern,
        previousAttempts,
        format,
        urgency,
        safetyConcern,
      });
      setPreparations((items) => [created, ...items]);
      selectPreparation(created);
    } catch (error) {
      showToast({ message: error.message, tone: "danger" });
    } finally {
      setSubmitting(false);
    }
  }

  async function retry() {
    if (!selected || selected.demo || retrying) return;
    setRetrying(true);
    try {
      const current = await api.retryConversationPreparation(selected.id);
      setSelected(current);
      setPreparations((items) => items.map((item) => item.id === current.id ? current : item));
    } catch (error) {
      showToast({ message: error.message, tone: "danger" });
    } finally {
      setRetrying(false);
    }
  }

  async function remove() {
    if (!selected || selected.demo || deleting) return;
    setDeleting(true);
    try {
      await api.deleteConversationPreparation(selected.id);
      setPreparations((items) => items.filter(({ id }) => id !== selected.id));
      setDeleteOpen(false);
      resetView();
      showToast({ message: "La preparación fue eliminada.", tone: "success" });
    } catch (error) {
      showToast({ message: error.message, tone: "danger" });
    } finally {
      setDeleting(false);
    }
  }

  if (selected?.status === "ready" && selected.plan) return <><ResultView preparation={selected} onBack={resetView} onDelete={() => setDeleteOpen(true)} />{deleteOpen && <ConfirmDialog title="¿Eliminar esta preparación?" description="Se borrarán la situación, la guía y sus ensayos con Justiniano. Esta acción no se puede deshacer." confirmLabel="Eliminar" busy={deleting} onClose={() => !deleting && setDeleteOpen(false)} onConfirm={remove} />}</>;
  if (selected?.status === "failed") return <ErrorView preparation={selected} retrying={retrying} onRetry={retry} onBack={resetView} />;
  if (selected) return <ProcessingView preparation={selected} onBack={resetView} />;

  const valid = relationship && counterpart.trim().length >= 2 && situation.trim().length >= 20 && goal.trim().length >= 5;
  return (
    <main className="civita-document-page conversation-page conversation-start-page">
      <header className="upload-heading conversation-start-heading">
        <span className="eyebrow">Preparar una conversación difícil</span>
        <h1>Decí lo importante.<br />Sin perder el rumbo.</h1>
        <p>Contanos qué necesitás hablar. Te ayudamos a definir tu objetivo, encontrar palabras claras y prepararte para distintos momentos.</p>
      </header>

      <div className="conversation-start-layout">
        <form className="conversation-form" onSubmit={submit}>
          <fieldset className="conversation-choice-group">
            <legend>¿En qué contexto es la conversación?</legend>
            <div className="conversation-relationship-grid">{relationships.map(({ id, label, description, icon: Icon }) => <button className={relationship === id ? "is-selected" : ""} type="button" aria-pressed={relationship === id} key={id} onClick={() => setRelationship(id)}><span><Icon size={17} /></span><div><strong>{label}</strong><small>{description}</small></div>{relationship === id && <Check size={14} />}</button>)}</div>
          </fieldset>

          <label className="claim-field"><span>¿Con quién necesitás hablar?</span><input value={counterpart} onChange={(event) => setCounterpart(event.target.value.slice(0, 300))} placeholder="Por ejemplo: mi jefe, mi hermana o el propietario" /></label>
          <label className="claim-field conversation-long-field"><span>¿Qué está pasando?</span><textarea value={situation} onChange={(event) => setSituation(event.target.value.slice(0, 6_000))} rows={6} placeholder="Contá los hechos con tus palabras: qué ocurrió, desde cuándo y qué conversaciones tuvieron." /><small>{situation.trim().length < 20 ? "Incluí un poco de contexto para poder ayudarte." : `${situation.length.toLocaleString("es-AR")} de 6.000 caracteres`}</small></label>
          <label className="claim-field"><span>¿Qué te gustaría lograr?</span><textarea value={goal} onChange={(event) => setGoal(event.target.value.slice(0, 1_500))} rows={3} placeholder="Por ejemplo: llegar a un acuerdo, pedir un cambio o dejar claro un límite." /></label>
          <div className="conversation-inline-fields">
            <label className="claim-field"><span>¿Qué es lo que más te preocupa? <small>Opcional</small></span><textarea value={concern} onChange={(event) => setConcern(event.target.value.slice(0, 1_500))} rows={4} placeholder="Por ejemplo: que se enoje o quedarme sin palabras." /></label>
            <label className="claim-field"><span>¿Qué intentaste hasta ahora? <small>Opcional</small></span><textarea value={previousAttempts} onChange={(event) => setPreviousAttempts(event.target.value.slice(0, 2_000))} rows={4} placeholder="Contá si ya hablaron y qué respuesta recibiste." /></label>
          </div>

          <fieldset className="conversation-choice-group compact"><legend>¿Cómo será?</legend><div className="conversation-format-grid">{formats.map(({ id, label, icon: Icon }) => <button className={format === id ? "is-selected" : ""} type="button" key={id} onClick={() => setFormat(id)}><Icon size={16} /> {label}</button>)}</div></fieldset>
          <fieldset className="conversation-choice-group compact"><legend>¿Cuándo?</legend><div className="conversation-pill-grid">{urgencyOptions.map(({ id, label }) => <button className={urgency === id ? "is-selected" : ""} type="button" key={id} onClick={() => setUrgency(id)}>{label}</button>)}</div></fieldset>

          <fieldset className="conversation-choice-group safety-choice"><legend>¿Te preocupa tu seguridad al hablar?</legend><div>{safetyOptions.map(({ id, label, description }) => <button className={safetyConcern === id ? "is-selected" : ""} type="button" key={id} onClick={() => setSafetyConcern(id)}><span>{safetyConcern === id && <Check size={13} />}</span><div><strong>{label}</strong><small>{description}</small></div></button>)}</div></fieldset>
          {safetyConcern === "afraid" && <p className="conversation-safety-input"><ShieldAlert size={17} /> Si existe un riesgo inmediato, priorizá alejarte y buscar ayuda local apropiada. No necesitás tener esta conversación a solas.</p>}

          <button className="analyze-button" type="submit" disabled={!valid || submitting}>{submitting ? <LoaderCircle className="is-spinning" size={18} /> : <Sparkles size={18} />}{submitting ? "Guardando la situación…" : "Preparar mi conversación"}<ArrowRight size={18} /></button>
          {!authEnabled && <button className="example-button" type="button" onClick={() => selectPreparation({ ...demoPreparation, demo: true })}>Ver un ejemplo completo</button>}
          <p className="privacy-note"><LockKeyhole size={14} />La situación queda protegida en tu espacio personal y no se comparte con la otra persona.</p>
        </form>

        <aside className="conversation-start-aside">
          <span className="eyebrow">Qué vas a recibir</span>
          <ol>
            <li><span>1</span><div><strong>Un objetivo claro</strong><small>Para no perder el rumbo si la charla se vuelve incómoda.</small></div></li>
            <li><span>2</span><div><strong>Frases que podés usar</strong><small>Una apertura, puntos principales y un cierre natural.</small></div></li>
            <li><span>3</span><div><strong>Preguntas y límites</strong><small>Cómo escuchar sin abandonar lo que necesitás decir.</small></div></li>
            <li><span>4</span><div><strong>Un ensayo privado</strong><small>Practicá distintos momentos con Justiniano.</small></div></li>
          </ol>
          <div><ShieldCheck size={19} /><p><strong>No buscamos que “ganes” una discusión.</strong> La guía te ayuda a hablar con claridad, escuchar y cuidar tus límites.</p></div>
        </aside>
      </div>

      {(loadingHistory || preparations.length > 0) && <section className="recent-documents conversation-history"><header><div><span className="eyebrow">Tus preparaciones</span><h2>Conversaciones recientes</h2></div></header>{loadingHistory ? <div className="history-loading"><LoaderCircle className="is-spinning" size={18} /> Cargando…</div> : <div className="document-list">{preparations.map((preparation) => <button type="button" key={preparation.id} onClick={() => selectPreparation(preparation)}><span className="document-list__icon"><MessageSquareText size={19} /></span><span><strong>{preparation.plan?.title || `Conversación con ${preparation.counterpart}`}</strong><small>{formatDate(preparation.createdAt)} · {formatLabel(relationships, preparation.relationship)}</small></span>{preparation.status === "ready" ? <CheckCircle2 className="is-ready" size={18} /> : preparation.status === "failed" ? <AlertCircle className="is-failed" size={18} /> : <Clock3 className="is-pending" size={18} />}<ChevronRight size={17} /></button>)}</div>}</section>}
    </main>
  );
}
